
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import ArticleTabs from './ArticleTabs';
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface Category {
  id: string;
  name: string;
}

interface Article {
  id: string;
  title: string;
  topic: string;
  category_id: string;
  date: string;
  slug: string;
  category?: {
    name: string;
  };
  publication_state: string;
}

export default function ArticleTable() {
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [articles, setArticles] = useState<Article[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCategories();
    fetchArticles();
  }, []);

  useEffect(() => {
    fetchArticles();
  }, [activeCategory]);

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('categories')
        .select('id, name')
        .order('name');

      if (error) throw error;
      setCategories(data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Failed to load categories');
    }
  };

  const fetchArticles = async () => {
    try {
      let query = supabase
        .from('articles')
        .select(`
          id, 
          title, 
          topic, 
          category_id, 
          date, 
          slug,
          publication_state
        `)
        .eq('publication_state', 'published')  // Only show published articles
        .order('date', { ascending: false });

      // Apply category filter if one is selected
      if (activeCategory) {
        query = query.eq('category_id', activeCategory);
      }

      const { data, error } = await query;

      if (error) throw error;
      setArticles(data || []);
    } catch (error) {
      console.error('Error fetching articles:', error);
      toast.error('Failed to load articles');
    } finally {
      setLoading(false);
    }
  };

  // Find category name by ID
  const getCategoryName = (categoryId: string): string => {
    const category = categories.find(cat => cat.id === categoryId);
    return category ? category.name : 'Unknown';
  };

  return (
    <div className="mt-16">
      <ArticleTabs 
        categories={categories}
        activeCategory={activeCategory} 
        onCategoryChange={setActiveCategory} 
      />
      
      <div className="glass-panel rounded-lg">
        <table className="w-full">
          <thead>
            <tr className="border-b border-white/10">
              <th className="text-left py-4 px-6 font-medium text-gray-300 text-sm">Title</th>
              <th className="text-left py-4 px-6 font-medium text-gray-300 text-sm hidden md:table-cell">Topic</th>
              <th className="text-left py-4 px-6 font-medium text-gray-300 text-sm hidden md:table-cell">Date</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={3} className="py-4 px-6 text-center">Loading articles...</td>
              </tr>
            ) : articles.length === 0 ? (
              <tr>
                <td colSpan={3} className="py-4 px-6 text-center">No articles found</td>
              </tr>
            ) : (
              articles.map((article) => (
                <tr key={article.id} className="table-row-hover border-b border-white/5 last:border-0">
                  <td className="py-4 px-6">
                    <Link to={`/article/${article.slug}`} className="text-white hover:text-blue-400 transition-colors">
                      {article.title}
                    </Link>
                  </td>
                  <td className="py-4 px-6 text-gray-400 hidden md:table-cell">
                    {article.topic}
                  </td>
                  <td className="py-4 px-6 text-gray-400 hidden md:table-cell">
                    {new Date(article.date).toLocaleDateString()}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
